import {
    Button,
    Table,
    Text,
    TextInput,
} from '@mantine/core';
import {
    IconChevronDown,
    IconChevronRight,
    IconDownload,
    IconFileDownload,
} from '@tabler/icons-react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import NavBar from '../../components/NavBar/NavBar';
import { STORES_PAIRS } from '../../constants/stores';
import '../Global.css';
import classes from './AutomateTr.module.css';

interface SelectedStyle {
    store: string;
    index: number;
}

interface SizeQuantity {
    size: string;
    quantity: number;
}

type DownloadQuantityType = 'allocated' | 'picked';

interface TableRow {
    storeName: string;
    styleNo: string[];
    supplierName: string;
    sizeQuantities: SizeQuantity[][];
    pickedQty: SizeQuantity[][];
    poNo: string;
    cost: number[];
    msrp: number[];
    color: string[];
    description: string[];
    notes: string[];
    totalQty: number[];
    status: string[];
    skuNumbers: string[][];
}

const AutomateTr = () => {
    const token = useSelector((state: any) => state.user.sessionId);

    const [tableData, setTableData] = useState<TableRow[]>([]);
    const [allocations, setAllocations] = useState<any[]>([]);
    const [poNo, setPoNo] = useState('');
    const [selectedRow, setSelectedRow] = useState('');
    const [selectedStyle, setSelectedStyle] = useState<SelectedStyle[]>([]);
    const [isSingleStorePo, setIsSingleStorePo] = useState(false);

    const storeList = Object.keys(STORES_PAIRS);

    /* =====================================================
       FETCH DATA
    ===================================================== */

    const fetchData = async () => {
        if (!poNo.trim()) {
            alert('Please enter a PO number.');
            return;
        }

        try {
            const response = await fetch(`/allocation/tr/${poNo.trim()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Unable to get transfer data.');
            }

            const data = await response.json();

            setAllocations(data);
            setSelectedRow('');
            setSelectedStyle([]);

            const formattedData: TableRow[] = storeList
                .map((store) => {
                    const dataFiltered = data.filter(
                        (allocation: any) =>
                            allocation.storeName.includes(store)
                    );

                    return {
                        storeName: store,
                        styleNo: dataFiltered.map((allocation: any) => allocation.style_no),
                        supplierName: String(dataFiltered[0]?.supplierName || ''),
                        sizeQuantities: dataFiltered.map((allocation: any) => allocation.sizeQuantities[
                            allocation.storeName.indexOf(store)
                        ]),
                        pickedQty: dataFiltered.map((allocation: any) => Array.isArray(allocation.pickedQty) ? allocation.pickedQty : []),
                        poNo: String(dataFiltered[0]?.po_no || ''),
                        cost: dataFiltered.map((allocation: any) => allocation.cost),
                        msrp: dataFiltered.map((allocation: any) => allocation.msrp),
                        color: dataFiltered.map((allocation: any) => allocation.color),
                        description: dataFiltered.map((allocation: any) => allocation.description),
                        notes: dataFiltered.map((allocation: any) => allocation.notes),
                        totalQty: dataFiltered.map((allocation: any) => {
                            const storeIndex = allocation.storeName.indexOf(store);
                            return allocation.sizeQuantities[storeIndex].reduce((total: number, item: SizeQuantity) =>
                                total +
                                Number(item.quantity || 0),
                                0
                            );
                        }),
                        status: dataFiltered.map(
                            (allocation: any) => {
                                const storeIndex = allocation.storeName.indexOf(store);
                                return allocation.allocation_status[storeIndex] === true ? 'Allocated' : 'Pending';
                            }
                        ),
                        skuNumbers: dataFiltered.map((allocation: any) => allocation.skuNumbers),
                    };
                })
                .filter(
                    (record) =>
                        record.totalQty.reduce(
                            (total: number, qty: number) =>
                                total + qty,
                            0
                        ) > 0
                );
            setIsSingleStorePo(formattedData.length === 1);
            setTableData(formattedData);
        } catch (error) {
            console.error('Error getting transfer data:', error);
            alert('Unable to get transfer data.');
        }
    };

    /* =====================================================
       STORE EXPANSION
    ===================================================== */

    const handleExpandRow = (store: string) => {
        if (selectedRow === store) {
            setSelectedRow('');
            /*
             * Remove expanded styles belonging to the store
             * when the store itself is collapsed.
             */
            setSelectedStyle((prev) => prev.filter((item) => item.store !== store));
            return;
        }
        setSelectedRow(store);
    };

    /* =====================================================
       INDIVIDUAL STYLE EXPANSION
    ===================================================== */

    const handleExpandStyle = (
        store: string,
        index: number
    ) => {
        setSelectedStyle((prev) => {
            const isExpanded = prev.some((item) => item.store === store && item.index === index);

            if (isExpanded) {
                return prev.filter((item) => !(item.store === store && item.index === index));
            }

            return [
                ...prev,
                { store, index, },
            ];
        });
    };

    const isStyleExpanded = (
        store: string,
        index: number
    ) => {
        return selectedStyle.some((item) => item.store === store && item.index === index);
    };

    /* =====================================================
       EXPAND / COLLAPSE ALL STYLES
    ===================================================== */

    const areAllStylesExpanded = (
        store: string,
        styleCount: number
    ) => {
        if (styleCount === 0) {
            return false;
        }

        return Array.from(
            { length: styleCount },
            (_, index) =>
                isStyleExpanded(
                    store,
                    index
                )
        ).every(Boolean);
    };

    const handleExpandAllStyles = (
        store: string,
        styleCount: number
    ) => {
        setSelectedStyle((prev) => {
            const allExpanded = Array.from(
                { length: styleCount },
                (_, index) =>
                    prev.some((item) => item.store === store && item.index === index)).every(Boolean);
            /*
             * Collapse every style for this store.
             */
            if (allExpanded) {
                return prev.filter((item) => item.store !== store);
            }
            /*
             * Keep expanded styles from other stores and
             * replace this store's list with every style.
             */
            const otherStores = prev.filter(
                (item) =>
                    item.store !== store
            );

            const allStyles: SelectedStyle[] =
                Array.from(
                    { length: styleCount },
                    (_, index) => ({
                        store,
                        index,
                    })
                );

            return [
                ...otherStores,
                ...allStyles,
            ];
        });
    };

    /* =====================================================
       EXPANDED SIZE / SKU DETAILS
    ===================================================== */

    const renderExpandedStyleDetails = (
        sizeQuantities: SizeQuantity[],
        skuNumbers: string[],
        pickedQty: SizeQuantity[]
    ) => {
        return (
            <Table
                withColumnBorders
                withRowBorders
                withTableBorder
                striped
                stickyHeader
                style={{
                    borderCollapse: 'collapse',
                }}
            >
                <Table.Thead>
                    <Table.Tr className={classes.expandedStyleTableHead}>
                        <Table.Th>Sku</Table.Th>
                        <Table.Th>Size</Table.Th>
                        <Table.Th>Allocated Qty</Table.Th>
                        {isSingleStorePo && (
                            <>
                                <Table.Th>Picked Qty</Table.Th>
                                <Table.Th>Difference</Table.Th>
                            </>
                        )}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {sizeQuantities?.map((item, index) => {
                        const sku = skuNumbers?.[index] || 'N/A';
                        const picked = pickedQty?.find(
                            (pickedItem) => pickedItem.size?.trim().toUpperCase() === item.size?.trim().toUpperCase()
                        );
                        const pickedQuantity = Number(picked?.quantity || 0);
                        const difference = pickedQuantity - Number(item.quantity || 0);

                        return (
                            <Table.Tr key={`${item.size}-${index}`}>
                                <Table.Td
                                    style={{
                                        backgroundColor: sku === 'N/A' ? '#B71C1C' : '',
                                        color: sku === 'N/A' ? 'white' : '',
                                    }}
                                >
                                    {sku}
                                </Table.Td>
                                <Table.Td>{item.size}</Table.Td>
                                <Table.Td>{item.quantity}</Table.Td>
                                {isSingleStorePo && (
                                    <>
                                        <Table.Td>{pickedQuantity}</Table.Td>
                                        <Table.Td>
                                            <Text
                                                fw={700}
                                                c={
                                                    difference < 0
                                                        ? 'red'
                                                        : difference > 0
                                                            ? 'green'
                                                            : 'dimmed'
                                                }
                                            >
                                                {difference > 0
                                                    ? `+${difference}`
                                                    : difference}
                                            </Text>
                                        </Table.Td>
                                    </>
                                )}
                            </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
        );
    };

    /* =====================================================
       EXPANDED STORE / STYLE TABLE
    ===================================================== */

    const renderExpandedRowDetails = (
        row: TableRow
    ) => {
        const allExpanded =
            areAllStylesExpanded(row.storeName, row.styleNo.length);

        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        marginBottom: '8px',
                    }}
                >
                    <Text
                        fw={700}
                        size="sm"
                    >
                        {row.styleNo.length}{' '}
                        Style
                        {row.styleNo.length !== 1 ? 's' : ''}
                    </Text>

                    <Button
                        size="xs"
                        variant="light"
                        onClick={() =>
                            handleExpandAllStyles(
                                row.storeName,
                                row.styleNo.length
                            )
                        }
                    >
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                    </Button>
                </div>

                <Table
                    withColumnBorders
                    withRowBorders
                    withTableBorder
                    striped
                    stickyHeader
                    style={{ borderCollapse: 'collapse' }}
                >
                    <Table.Thead>
                        <Table.Tr className={classes.expandedTableHead}>
                            <Table.Th style={{ width: '35px' }} />
                            <Table.Th>Style</Table.Th>
                            <Table.Th>Supplier Name</Table.Th>
                            <Table.Th>Total Quantity</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Cost</Table.Th>
                            <Table.Th>MSRP</Table.Th>
                            <Table.Th>Description</Table.Th>
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                        {row.styleNo.map((styleNo, index) => {
                            const expanded = isStyleExpanded(row.storeName, index);
                            return (
                                <React.Fragment
                                    key={`${row.storeName}-${styleNo}-${index}`}
                                >
                                    <Table.Tr
                                        onClick={() =>
                                            handleExpandStyle(row.storeName, index)
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Table.Td>
                                            {expanded ? (
                                                <IconChevronDown size={16} />
                                            ) : (
                                                <IconChevronRight size={16} />
                                            )}
                                        </Table.Td>

                                        <Table.Td><Text fw={600} size="sm">{styleNo}</Text></Table.Td>
                                        <Table.Td>{row.supplierName}</Table.Td>
                                        <Table.Td>{row.totalQty[index]}</Table.Td>
                                        <Table.Td>{row.status[index]}</Table.Td>
                                        <Table.Td>{row.cost[index]}</Table.Td>
                                        <Table.Td>{row.msrp[index]}</Table.Td>
                                        <Table.Td>{row.description[index]}</Table.Td>
                                    </Table.Tr>
                                    {expanded && (
                                        <Table.Tr>
                                            <Table.Td
                                                colSpan={8}
                                                style={{ padding: '8px' }}
                                            >
                                                {renderExpandedStyleDetails(
                                                    row.sizeQuantities[index],
                                                    row.skuNumbers[index],
                                                    row.pickedQty[index]
                                                )}
                                            </Table.Td>
                                        </Table.Tr>
                                    )}
                                </React.Fragment>
                            );
                        }
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        );
    };

    /* =====================================================
       GENERATE TRANSFER FILE
    ===================================================== */

    const generateDataString = (
        store: string,
        quantityType: DownloadQuantityType = 'allocated'
    ) => {
        let dataString = '';

        allocations
            .filter((allocation) =>
                allocation.storeName.includes(store)
            )
            .forEach((allocation) => {
                const storeIndex =
                    allocation.storeName.indexOf(store);

                const sizes: SizeQuantity[] = allocation.sizeQuantities[storeIndex] || [];
                sizes.forEach((size, sizeIndex) => {
                    const sku = allocation.skuNumbers?.[sizeIndex] || 'N/A';
                    let quantity = Number(size.quantity || 0);
                    if (quantityType === 'picked' && isSingleStorePo) {
                        const picked = allocation.pickedQty?.find((pickedItem: SizeQuantity) =>
                            pickedItem.size?.trim().toUpperCase() === size.size?.trim().toUpperCase()
                        );
                        quantity = Number(picked?.quantity || 0);
                    }
                    if (quantity > 0 && sku !== 'N/A') {
                        dataString += `${sku},${quantity},,\n`;
                    }
                });
            });

        return dataString;
    };

    /* =====================================================
       DOWNLOAD SINGLE STORE
    ===================================================== */

    const handleDownload = (
        store: string,
        type: DownloadQuantityType
    ) => {
        const dataString =
            generateDataString(
                store,
                type
            );

        if (!dataString) {
            alert('No data available to download.');
            return;
        }

        const formattedPoNo = poNo.replace(/[^a-zA-Z0-9]/g, '_');
        const formattedStoreName = store.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `${formattedPoNo}_${formattedStoreName}.txt`;
        const blob = new Blob([dataString], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(url);
    };

    /* =====================================================
       DOWNLOAD ALL STORES
    ===================================================== */

    const handleDownloadAll = async () => {
        const zip = new JSZip();

        const allStores = tableData.filter((data) =>
            data.totalQty.reduce((total, qty) => total + qty, 0) > 0
        ).map((data) => data.storeName);

        if (allStores.length === 0) {
            alert('No data available to download.');
            return;
        }

        for (const store of allStores) {
            const dataString = generateDataString(store);
            if (!dataString) {
                alert(`No data available to download for ${store}.`);
                return;
            }
            const formattedPoNo = poNo.replace(/[^a-zA-Z0-9]/g, '_');
            const formattedStoreName = store.replace(/[^a-zA-Z0-9]/g, '_');
            const fileName = `${formattedPoNo}_${formattedStoreName}.txt`;
            zip.file(fileName, dataString);
        }

        const formattedPoNo = poNo.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `${formattedPoNo}_ALL_STORES.zip`;
        const content = await zip.generateAsync({ type: 'blob', });
        saveAs(content, fileName);
    };

    /* =====================================================
       UI
    ===================================================== */

    return (
        <div>
            <NavBar />

            <div className="main">
                <h2 className="title">Automate Transfer</h2>
                <div className={classes.upperSection}>
                    <TextInput
                        placeholder="PO Number"
                        value={poNo}
                        onChange={(event) =>
                            setPoNo(event.currentTarget.value)
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                fetchData();
                            }
                        }}
                        style={{ flexGrow: 1 }}
                    />

                    <Button onClick={fetchData}>Get Data</Button>
                    <Button onClick={handleDownloadAll} leftSection={<IconFileDownload size={18} />} disabled={tableData.length === 0}>
                        Download All
                    </Button>
                </div>
                <div>
                    <Table
                        striped
                        withColumnBorders
                        withTableBorder
                        stickyHeader
                        className={classes.table}
                    >
                        <Table.Thead
                            className={classes.tableHead}
                        >
                            <Table.Tr>
                                <Table.Th style={{ width: '40px' }} />
                                <Table.Th>Store Name</Table.Th>
                                <Table.Th>Styles</Table.Th>
                                <Table.Th style={{ width: '160px' }}>Download TR File</Table.Th>
                            </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                            {tableData.map(
                                (item) => {
                                    const expanded = selectedRow === item.storeName;

                                    return (
                                        <React.Fragment key={item.storeName} >
                                            <Table.Tr
                                                onClick={() => handleExpandRow(item.storeName)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Table.Td>
                                                    {expanded ? (
                                                        <IconChevronDown size={18} />
                                                    ) : (
                                                        <IconChevronRight size={18} />
                                                    )}
                                                </Table.Td>
                                                <Table.Td><Text fw={600}>{item.storeName}</Text></Table.Td>
                                                <Table.Td>{item.styleNo.length}</Table.Td>
                                                <Table.Td style={{ textAlign: 'center', display: 'flex', justifyContent: 'space-between' }}   >
                                                    <Button
                                                        size="xs"
                                                        variant="light"
                                                        leftSection={<IconDownload size={16} />}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleDownload(item.storeName, 'allocated');
                                                        }}
                                                    >
                                                        Download
                                                    </Button>
                                                    {isSingleStorePo && (
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="green"
                                                            leftSection={
                                                                <IconDownload size={16} />
                                                            }
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleDownload(item.storeName, 'picked');
                                                            }}
                                                        >
                                                            Picked Qty
                                                        </Button>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>

                                            {expanded && (
                                                <Table.Tr>
                                                    <Table.Td
                                                        colSpan={4}
                                                        style={{ padding: '10px' }}
                                                    >
                                                        {renderExpandedRowDetails(item)}
                                                    </Table.Td>
                                                </Table.Tr>
                                            )}
                                        </React.Fragment>
                                    );
                                }
                            )}

                            {tableData.length === 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text ta="center" c="dimmed" py="xl" >
                                            Enter a PO number to load transfer data.
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default AutomateTr;